import { useCallback, useState } from "react";
import { FormProvider, SubmitHandler } from "react-hook-form";
import { useNavigate } from "react-router";

import DocumentationIconButton from "components/DocumentationIconButton";
import Markdown from "components/markdown/Markdown";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import CardCollapsible from "ds/components/Card/Collapsible";
import Tooltip from "ds/components/Tooltip";
import { showFullDescription } from "ds/components/FullDescriptionDrawer";
import FullScreenModalBody from "ds/components/FullScreenModal/Body";
import FullScreenModalFooter from "ds/components/FullScreenModal/Footer";
import Typography from "ds/components/Typography";
import { useFormValidations } from "hooks/useFormValidations";
import { generateCallbackUrl } from "shared/Templates/utils";
import { TemplateVersion, BlueprintState, TemplateDeploymentType } from "types/generated";
import { getDocsUrl } from "utils/getDocsUrl";

import DeploymentForm from "../components/DeploymentForm";
import { VersionTemplateFormFields } from "../components/DeploymentForm/types";
import useDeploymentForm from "../components/DeploymentForm/useDeploymentForm";
import { DEPRECATED_VERSION_DEPLOY_TOOLTIP } from "../Deployments/constants";
import { showDeployConfirmationModal } from "./DeployConfirmationModal";
import styles from "./styles.module.css";

type DeployFormProps = {
  version: TemplateVersion;
  templateName: string;
  templateId: string;
  isFromMarketplace?: boolean;
  deploymentType?: TemplateDeploymentType;
};

const DeployForm = ({
  version,
  templateName,
  templateId,
  isFromMarketplace,
  deploymentType,
}: DeployFormProps) => {
  const navigate = useNavigate();

  const [isInstructionsCollapsed, setIsInstructionsCollapsed] = useState(false);

  const deploymentInstructions = version.instructions;
  const isVersionDeprecated = !!version.deprecatedAt;

  const { builderForm, onSubmit, deploymentCreateLoading, hasErrors, hasPreview, errors, inputs } =
    useDeploymentForm({
      version,
      deploymentType,
      submitCallback: (deploymentId) =>
        navigate(generateCallbackUrl(isFromMarketplace, templateId, version.id, deploymentId)),
    });

  const handleOpenFullDescription = () => {
    showFullDescription({ description: version.instructions });
  };

  const {
    handleSubmit,
    formState: { isDirty },
  } = builderForm;
  const runFormValidations = useFormValidations(builderForm);

  const handleDeploy: SubmitHandler<VersionTemplateFormFields> = useCallback(
    async (formData) => {
      if (isVersionDeprecated) return;

      const isValid = await runFormValidations();
      if (!isValid) return;

      showDeployConfirmationModal({
        templateName,
        deploymentName: formData.name,
        onDeploy: () => onSubmit(formData),
      });
    },
    [isVersionDeprecated, templateName, onSubmit, runFormValidations]
  );

  const deploySubmitDisabled = deploymentCreateLoading || !isDirty || isVersionDeprecated;

  return (
    <FormProvider {...builderForm}>
      <form className={styles.form} onSubmit={handleSubmit(handleDeploy)}>
        <FullScreenModalBody noMaxWidth>
          <Box justify="center" gap="x-large" className={styles.contentWrapper} fullWidth>
            <Box
              direction="column"
              shrink="0"
              padding="x-large"
              align="center"
              className={styles.formSection}
            >
              <Box direction="column" className={styles.formFields}>
                <DeploymentForm
                  item={version}
                  isPublished={version.state === BlueprintState.Published}
                  hasPreview={hasPreview}
                  hasErrors={hasErrors}
                  errors={errors}
                  inputs={inputs}
                  onOpenFullDescription={handleOpenFullDescription}
                />
              </Box>
            </Box>

            <Box
              direction="column"
              shrink="0"
              padding="x-large"
              className={styles.instructionsSection}
            >
              <CardCollapsible
                title="Instructions"
                isCollapsed={isInstructionsCollapsed}
                onToggle={() => setIsInstructionsCollapsed(!isInstructionsCollapsed)}
                ariaLevel={2}
                ariaLabel="Deployment instructions"
              >
                {deploymentInstructions ? (
                  <Markdown markup={deploymentInstructions} />
                ) : (
                  <Typography tag="div" variant="p-body2" color="secondary">
                    No deployment instructions provided for this template.
                  </Typography>
                )}
              </CardCollapsible>
            </Box>
          </Box>
        </FullScreenModalBody>

        <FullScreenModalFooter justify="between">
          <DocumentationIconButton
            href={getDocsUrl("/concepts/template/deployments")}
            tooltipText="Go to templates documentation"
            size="small"
          />
          <Tooltip
            active={isVersionDeprecated}
            placement="top"
            widthMode="maxWidthSm"
            on={(props) => (
              <Button
                {...props}
                variant="primary"
                type="submit"
                loading={deploymentCreateLoading}
                disabled={deploySubmitDisabled}
              >
                Deploy
              </Button>
            )}
          >
            {DEPRECATED_VERSION_DEPLOY_TOOLTIP}
          </Tooltip>
        </FullScreenModalFooter>
      </form>
    </FormProvider>
  );
};

export default DeployForm;
